/**
 * 研究所の中の「節」。記事の category から URL の1階層目が決まる。
 *
 * # なぜ平場から節に分けたか
 *
 * もとは全部 `/lab/<slug>` だった。取材も講師の話も代表の記事も同じ高さに
 * 並ぶので、束ねる受け皿が無く、「ゆめさくエンジニア 評判」のような
 * まとまりで探している人の入口が作れない。節を切ると親ページが立ち、
 * パンくずが1段深くなり、同じ種類の記事どうしが内部リンクで繋がる。
 *
 * # slug から真ん中を落とした理由
 *
 * `yumesaku-interview-01-aine` の `interview` はパスに上がったので、
 * slug に残すと `/lab/voices/yumesaku-interview-01-aine` と同じ語を2回言う。
 * `yumesaku-` も、yumesaku.tech の中では全記事に等しく付く語なので落とす。
 *
 * # 引っ越し表を消してはいけない
 *
 * {@link LAB_SLUG_MOVES} は旧 URL からの 301 の唯一の材料。note の連載や
 * 外から張られたリンクは旧 URL のままなので、**この表は永久に残す**。
 * 消すと、そのリンクは黙って 404 になる。
 */

export type LabSectionPath = "voices" | "teachers" | "founder";

export type LabSection = {
  /** URL は /lab/<path>/<slug> */
  path: LabSectionPath;
  /** articles.category と 1 対 1。ここが唯一の振り分けの根拠 */
  category: string;
  /** パンくずと親ページの見出し */
  label: string;
  /** 親ページのリード文 */
  lead: string;
};

export const LAB_SECTIONS: readonly LabSection[] = [
  {
    path: "voices",
    category: "受講生インタビュー",
    label: "受講生インタビュー",
    lead: "入会を決めた人が、何に迷って何を選んだのかを、本人の言葉のまま載せています。",
  },
  {
    path: "teachers",
    category: "講師インタビュー",
    label: "講師インタビュー",
    lead: "教えている側が、どこでつまずく人を見てきたのかを聞いています。",
  },
  {
    path: "founder",
    category: "代表記事",
    label: "代表の記事",
    lead: "運営として都合の悪いことも含めて、代表が自分の言葉で書いています。",
  },
] as const;

export function labSectionForCategory(
  category: string | null | undefined,
): LabSection | null {
  if (!category) return null;
  return LAB_SECTIONS.find((s) => s.category === category) ?? null;
}

export function labSectionByPath(path: string | undefined): LabSection | null {
  if (!path) return null;
  return LAB_SECTIONS.find((s) => s.path === path) ?? null;
}

/**
 * 記事の置き場所。節に属さない記事は今までどおり `/lab/<slug>` に出る。
 *
 * category を持たない呼び出し側から使うと平場の URL に落ちる。
 * **それは 301 になる。** 節に属する記事の `/lab/<slug>` は旧 URL で、
 * 301 で節の下へ送られる。一覧の projection では category を必ず渡すこと。
 */
export function labArticlePath(article: {
  slug: string;
  category?: string | null;
}): string {
  const section = labSectionForCategory(article.category);
  return section ? `/lab/${section.path}/${article.slug}` : `/lab/${article.slug}`;
}

/**
 * 旧 URL から新 URL への引っ越し表。**旧 URL の 301 の材料なので消さない。**
 *
 * 行き先をここに丸ごと持たせているのは、301 を DB を読まずに返せるようにする
 * ため。category から引く形にすると、slug を付け替えたあとの旧 URL は
 * 「その slug の記事がもう無い」ので category を引けず、301 を返せなくなる。
 *
 * slug が変わらないまま節だけ移った記事 (`career-guarantee-dark-side` など) も
 * 載せる。載っていないと、その記事だけ旧 URL が 404 のまま残る。
 */
export const LAB_SLUG_MOVES: Readonly<
  Record<string, { path: LabSectionPath; slug: string }>
> = {
  // 受講生インタビュー
  "yumesaku-interview-01-aine": { path: "voices", slug: "01-aine" },
  "yumesaku-interview-02-tabi": { path: "voices", slug: "02-tabi" },
  "yumesaku-interview-03-miyazaki": { path: "voices", slug: "03-miyazaki" },
  "yumesaku-interview-04-yuuki": { path: "voices", slug: "04-yuuki" },
  "yumesaku-interview-05-asami": { path: "voices", slug: "05-asami" },
  "yumesaku-interview-06-nabe": { path: "voices", slug: "06-nabe" },
  "yumesaku-interview-07-freelance": { path: "voices", slug: "07-freelance" },
  "yumesaku-interview-08-seki": { path: "voices", slug: "08-seki" },
  "yumesaku-interview-09-yuuki": { path: "voices", slug: "09-yuuki" },
  "yumesaku-interview-10-potato": { path: "voices", slug: "10-potato" },
  "yumesaku-first-cohort-now": { path: "voices", slug: "first-cohort-now" },
  // 講師インタビュー
  "yumesaku-instructor-01-ikuta": { path: "teachers", slug: "01-ikuta" },
  "yumesaku-instructor-02-obi": { path: "teachers", slug: "02-obi" },
  "yumesaku-instructor-03-ue": { path: "teachers", slug: "03-ue" },
  "yumesaku-instructor-04-yamamoto": { path: "teachers", slug: "04-yamamoto" },
  // 代表の記事。名前が変わらないものも、節が変わるので載せる
  "yumesaku-reputation-full": { path: "founder", slug: "reputation-full" },
  "yumesaku-not-for-you": { path: "founder", slug: "not-for-you" },
  "career-guarantee-dark-side": {
    path: "founder",
    slug: "career-guarantee-dark-side",
  },
  "three-months-engineer-lowest-bar": {
    path: "founder",
    slug: "three-months-engineer-lowest-bar",
  },
  "ai-side-income-reality": { path: "founder", slug: "ai-side-income-reality" },
  "career-change-hype-is-over": {
    path: "founder",
    slug: "career-change-hype-is-over",
  },
  "first-three-months-who-stays": {
    path: "founder",
    slug: "first-three-months-who-stays",
  },
  "it-skills-pay-off": { path: "founder", slug: "it-skills-pay-off" },
  "legendary-appliance-engineer": {
    path: "founder",
    slug: "legendary-appliance-engineer",
  },
  "remote-work-not-decided-by-code": {
    path: "founder",
    slug: "remote-work-not-decided-by-code",
  },
  "self-taught-engineer-reality": {
    path: "founder",
    slug: "self-taught-engineer-reality",
  },
  "subsidized-school-reality": {
    path: "founder",
    slug: "subsidized-school-reality",
  },
  "web-designer-from-zero-reality": {
    path: "founder",
    slug: "web-designer-from-zero-reality",
  },
  /* 柱の4本。2026-09-22 に category を 代表記事 へ移したので、
     それまでの平場の URL をここで受ける */
  "inexperienced-engineer-reality": {
    path: "founder",
    slug: "inexperienced-engineer-reality",
  },
  "study-hours-18-months": { path: "founder", slug: "study-hours-18-months" },
  "programming-school-selection": {
    path: "founder",
    slug: "programming-school-selection",
  },
  "programming-pc-buying-guide": {
    path: "founder",
    slug: "programming-pc-buying-guide",
  },
  "why-good-engineers-avoid-schools": {
    path: "founder",
    slug: "why-good-engineers-avoid-schools",
  },
};

/** 旧 URL で来たときの行き先。引っ越していない slug なら null */
export function labLegacyPath(slug: string): string | null {
  const moved = LAB_SLUG_MOVES[slug];
  return moved ? `/lab/${moved.path}/${moved.slug}` : null;
}

/**
 * 新 slug から旧 slug。**DB の付け替えが終わるまでの逃げ道。**
 *
 * コードを先に出して DB をあとから付け替えるので、その間だけ
 * `/lab/voices/01-aine` は DB に無い slug を指す。ここで旧 slug に落として
 * 引き直せば、デプロイと DB 更新のあいだに穴が空かない。
 */
export const LAB_LEGACY_SLUG_OF: Readonly<Record<string, string>> =
  Object.fromEntries(
    Object.entries(LAB_SLUG_MOVES).map(([from, to]) => [to.slug, from]),
  );

/**
 * 公開されている slug の集合に、その記事が居るか。
 *
 * **コードを先に出して DB の slug をあとから付け替えるための橋。**
 * その間だけ、手元の一覧は新しい slug を持ち DB は古い slug を持つ。
 * 素朴に `published.has(slug)` で見ると、/lab の受講生インタビューの節が
 * まるごと消える。付け替えが済んだら、この関数は消してよい。
 */
export function labSlugPublished(
  published: ReadonlySet<string>,
  slug: string,
): boolean {
  if (published.has(slug)) return true;
  const legacy = LAB_LEGACY_SLUG_OF[slug];
  return legacy ? published.has(legacy) : false;
}
